import { useCallback, useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Search, ListChecks } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Conversation } from "./Conversation";
import { NextFive } from "./NextFive";
import { StatusPill, Empty } from "./ui";
import { shortWhen } from "@/lib/format";

const FILTERS = [["", "Todos"], ["oracao", "Pedidos de oração"], ["fotografia", "Fotografias"], ["testemunho", "Testemunhos"],
  ["comprovante", "Comprovantes"], ["deus_provera", "Deus Proverá"], ["pendentes", "Pendentes"], ["respondidos", "Respondidos"]];
const TITLES = { informacoes: "Informações sobre a igreja", outras: "Outras mensagens", aguardando: "Aguardando classificação" };

const CatChips = ({ ids }) => {
  const { categories } = useOutletContext();
  const names = ids.map((id) => categories.find((c) => c.id === id)?.name).filter(Boolean);
  return names.map((n) => <span key={n} className="rounded bg-zap-deep/10 px-1.5 py-0.5 text-[11px] font-semibold text-zap-deep">{n}</span>);
};

const Row = ({ c, active, onClick }) => (
  <button data-testid={`conversation-row-${c.id}`} onClick={onClick}
    className={`flex w-full gap-3 border-b border-zap-line px-4 py-3 text-left transition-colors ${active ? "bg-zap-goldsoft/60" : "hover:bg-zap-mist"}`}>
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-zap-deep/10 font-display font-semibold text-zap-deep">
      {c.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[15px] font-semibold text-zap-ink">{c.name}</span>
        <span className="shrink-0 text-xs text-muted-foreground">{shortWhen(c.last_message_at)}</span>
      </span>
      <span className="mt-0.5 flex items-center gap-2">
        <span className="flex-1 truncate text-sm text-muted-foreground">{c.last_message_preview}</span>
        {c.unread_count > 0 && <span data-testid={`conversation-unread-${c.id}`} className="flex h-5 min-w-5 items-center justify-center rounded-full bg-zap-green px-1.5 text-xs font-bold text-white">{c.unread_count}</span>}
      </span>
      <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"><StatusPill status={c.status} /><CatChips ids={c.last_category_ids || []} /><span>{c.city}/{c.state}</span>{c.deletion_requested_at && <span className="font-semibold text-red-700">Pediu exclusão</span>}</span>
    </span>
  </button>
);

export default function Inbox() {
  const { api } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const selected = params.get("p");
  const filter = params.get("f") || "";
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [nextOpen, setNextOpen] = useState(false);
  const go = (next) => setParams(Object.fromEntries(Object.entries({ f: filter, p: selected, ...next }).filter(([, v]) => v)));

  const load = useCallback(() => {
    const query = { filter: filter || undefined, q: q || undefined };
    return api.get("/admin/conversations", { params: query }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [api, filter, q]);

  useEffect(() => {
    const t0 = setTimeout(load, q ? 300 : 0);
    const t = setInterval(load, 10000);
    return () => { clearTimeout(t0); clearInterval(t); };
  }, [load, q]);

  return (
    <div className="flex h-full min-h-0">
      <div className={`${selected ? "hidden lg:flex" : "flex"} w-full flex-col border-r border-zap-line bg-white lg:w-[320px] lg:shrink-0 2xl:w-[380px]`}>
        <div className="space-y-3 border-b border-zap-line p-4">
          <div className="flex items-center justify-between gap-2">
            <h1 data-testid="inbox-title" className="font-display text-xl font-semibold text-zap-ink">{TITLES[filter] || FILTERS.find(([v]) => v === filter)?.[1].replace("Todos", "Conversas") || "Conversas"}</h1>
            <button data-testid="prepare-next-five" onClick={() => setNextOpen(true)} className="flex h-9 items-center gap-1.5 rounded-md border border-zap-gold/50 bg-zap-goldsoft px-2.5 text-xs font-bold text-[#7A5A14] hover:bg-[#efe2c4]"><ListChecks className="h-4 w-4" /> PREPARAR PRÓXIMOS CINCO</button>
          </div>
          <NextFive open={nextOpen} onClose={() => { setNextOpen(false); load(); }} onChanged={load} />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="inbox-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, WhatsApp, cidade ou estado" className="h-11 pl-9 text-[15px]" />
          </div>
          <div className="flex flex-wrap gap-1">
            {FILTERS.map(([v, l]) => (
              <button key={v || "all"} data-testid={`inbox-filter-${v || "all"}`} onClick={() => go({ f: v, p: null })}
                className={`rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${filter === v ? "bg-zap-deep text-white" : "bg-zap-mist text-zap-ink/75 hover:bg-zap-line"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div data-testid="conversation-list" className="scroll-thin min-h-0 flex-1 overflow-y-auto">
          {list === null ? <Empty>Carregando…</Empty> : list.length === 0 ? <Empty testId="inbox-empty">Nenhuma conversa encontrada.</Empty> :
            list.map((c) => <Row key={c.id} c={c} active={c.id === selected} onClick={() => go({ p: c.id })} />)}
        </div>
      </div>
      <div className={`${selected ? "flex" : "hidden lg:flex"} min-w-0 flex-1`}>
        {selected ? <Conversation key={selected} pid={selected} onBack={() => go({ p: null })} onChanged={load} /> :
          <div className="chat-canvas flex flex-1 items-center justify-center"><p className="rounded-md bg-white/90 px-4 py-2 text-[15px] text-muted-foreground">Selecione uma conversa para atender.</p></div>}
      </div>
    </div>
  );
}
